'use client';

import React, { useState, Suspense } from 'react';
import { Content, Heading } from 'mdast';
import Markdown, { FootnoteHandler } from '../Markdown';
import { ChunkDocContext } from '../../lib/parseMarkdown';
import { useSearchParams } from 'next/navigation';
import slugify from 'slugify';

interface TabBoxesProps {
  children: Content[];
  docContext?: ChunkDocContext;
  FootnoteHandler?: FootnoteHandler
}

interface TabBoxesNoQueryProps {
  docContext?: ChunkDocContext;
  FootnoteHandler?: FootnoteHandler;
  tabs: { title: string; content: Content[] }[];
  activeTab: number | null;
  setActiveTab: React.Dispatch<React.SetStateAction<number | null>>;
}

interface TabBoxesQueryProps {
  docContext?: ChunkDocContext;
  FootnoteHandler?: FootnoteHandler;
  tabs: { title: string; content: Content[] }[];
  activeTab: number | null;
  setActiveTab: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * Extracts text content from a node
 */
function getTextFromNode(node: Content): string {
  if (node.type === 'text') {
    return (node as any).value;
  }
  if (("children" in node) && node.children && Array.isArray(node.children)) {
    return node.children.map(getTextFromNode).join(' ');
  }
  return '';
}

const TabBoxesNoQuery = (props: TabBoxesNoQueryProps) => {
  const { docContext, tabs, FootnoteHandler, activeTab, setActiveTab } = props;
  return (
    <div className="tab-boxes mt-7">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab, index) => (
          <button
            key={tab.title}
            className={`p-1 text-start px-2 border border-[1.5px] border-black rounded-md text-[1.2rem] whitespace-nowrap ${activeTab === index && 'bg-black text-white'
              }`}
            onClick={() => setActiveTab(prev => prev === index ? null : index)}
            id={`tab-box-${slugify(tab.title, { lower: true, strict: true })}`}
          >
            {tab.title}
          </button>
        ))}
      </div>

      <div className="pt-2 font-sans">
        {tabs.map((tab, index) => (
          <div
            key={tab.title}
            className={`
              rounded-md overflow-hidden [interpolate-size:allow-keywords] 
              [transition-property:height,opacity] [transition-duration:300ms] [transition-timing-function:ease-in-out]
              overflow-hidden [box-sizing:border-box] overflow-hidden outline outline-[1.5px] outline-black
              ${activeTab === index ? 'h-auto opacity-100' : 'opacity-0 h-[0px]'}`}
          >
            <div className="p-2 [&>p]:mt-0 [&>*:last-child]:mb-0">
              <Markdown nodes={tab.content} docContext={docContext} FootnoteHandler={FootnoteHandler} />
            </div>
          </div>
        ))}
      </div>
      
    </div>
  );
};


const TabBoxesQuery = (props: TabBoxesQueryProps) => {
  const { docContext, tabs, FootnoteHandler, activeTab } = props;
  const searchParams = useSearchParams();
  const preSelectedTab = searchParams?.get('tab') || null;
  const preSelectedTabIndex = preSelectedTab ? tabs.findIndex(tab => slugify(tab.title, { lower: true, strict: true }) === preSelectedTab) : null;
  const [internalActiveTab, setInternalActiveTab] = useState<number | null>(activeTab ?? preSelectedTabIndex);

  return <TabBoxesNoQuery docContext={docContext} FootnoteHandler={FootnoteHandler} tabs={tabs} activeTab={internalActiveTab} setActiveTab={setInternalActiveTab} />
};

/**
 * Creates tabs from h2 headings in markdown content
 */
export default function TabBoxes({ children, docContext, FootnoteHandler }: TabBoxesProps) {
  const tabs: { title: string; content: Content[] }[] = [];
  let currentTab: { title: string; content: Content[] } | null = null;
  
  children.forEach((node) => {
    if (node.type === 'heading' && (node as Heading).depth === 2) {
      // If we were already building a tab, save it
      if (currentTab) {
        tabs.push(currentTab);
      }
      
      // Start a new tab with this heading
      const heading = node as Heading;
      const title = heading.children.map(getTextFromNode).join(' ');
      currentTab = { title, content: [] };
    } else if (currentTab) {
      // Add content to the current tab
      currentTab.content.push(node);
    }
  });
  
  // Don't forget to add the last tab
  if (currentTab) {
    tabs.push(currentTab);
  }

  const [activeTab, setActiveTab] = useState<number | null>(null);

  if (tabs.length === 0) {
    return null;
  }

  return <Suspense fallback={<TabBoxesNoQuery docContext={docContext} FootnoteHandler={FootnoteHandler} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />}>
    <TabBoxesQuery docContext={docContext} FootnoteHandler={FootnoteHandler} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
  </Suspense>
} 
